import { entityLabel } from '../../../../shared/utils/entitySelection';
import { useMemo, useState } from 'react';
import { createContractDocument } from '@/modules/documents/services';
import { getInitialContractForm } from '@/modules/documents/utils';
import { toast } from '../../../../shared/hooks/use-toast';

export function useContractForm({ clients, onCreate }) {
  const [form, setForm] = useState(getInitialContractForm());
  const [isSubmitting, setIsSubmitting] = useState(false);

  const totals = useMemo(() => {
    const totalNominale = form.righeCrediti.reduce(
      (sum, row) => sum + Number(row.valoreNominale || 0),
      0
    );
    const totalPrezzoAcquisto = form.righeCrediti.reduce(
      (sum, row) => sum + Number(row.prezzoAcquisto || 0),
      0
    );

    return {
      totalNominale,
      totalPrezzoAcquisto,
    };
  }, [form.righeCrediti]);

  function updateField(field, value) {
    setForm((prev) => {
      if (field === 'clientIds') {
        const selected = value.map((id) => clients.find((client) => String(client.id) === String(id))).filter(Boolean);
        return { ...prev, clientIds: value, clientId: value[0] || '', client: selected.map(entityLabel).join('; '), firmatario: selected.map((client) => client.name || entityLabel(client)).join('; ') };
      }
      if (field === 'clientId') {
        const selectedClient = clients.find((client) => String(client.id) === String(value));
        return {
          ...prev,
          clientId: value,
          client: selectedClient?.name || '',
          firmatario: selectedClient?.name || '',
        };
      }

      return {
        ...prev,
        [field]: value,
      };
    });
  }

  function updateCreditRow(id, field, value) {
    setForm((prev) => ({
      ...prev,
      righeCrediti: prev.righeCrediti.map((row) =>
        row.id === id ? { ...row, [field]: value } : row
      ),
    }));
  }

  function addCreditRow() {
    setForm((prev) => ({
      ...prev,
      righeCrediti: [
        ...prev.righeCrediti,
        {
          id: prev.righeCrediti.length + 1,
          tipoCredito: '',
          anno: '',
          descrizione: '',
          valoreNominale: '',
          prezzoAcquisto: '',
        },
      ],
    }));
  }

  function removeCreditRow(id) {
    setForm((prev) => ({
      ...prev,
      righeCrediti:
        prev.righeCrediti.length > 1
          ? prev.righeCrediti.filter((row) => row.id !== id)
          : prev.righeCrediti,
    }));
  }

  function updateTranche(id, field, value) {
    setForm((prev) => ({
      ...prev,
      tranche: prev.tranche.map((row) => (row.id === id ? { ...row, [field]: value } : row)),
    }));
  }

  function addTranche() {
    setForm((prev) => ({
      ...prev,
      tranche: [
        ...prev.tranche,
        {
          id: prev.tranche.length + 1,
          percentuale: '',
          importo: '',
          dataScadenza: '',
          condizione: '',
        },
      ],
    }));
  }

  function removeTranche(id) {
    setForm((prev) => ({
      ...prev,
      tranche:
        prev.tranche.length > 1
          ? prev.tranche.filter((row) => row.id !== id)
          : prev.tranche,
    }));
  }

  function reset() {
    setForm(getInitialContractForm());
  }

  async function submit() {
    setIsSubmitting(true);
    const result = await createContractDocument(form);
    setIsSubmitting(false);

    if (!result.ok) {
      toast({ title: 'Contratto non creato', description: result.message, variant: 'destructive' });
      return false;
    }

    await onCreate(result.data.document);
    reset();
    toast({ title: 'Contratto creato', description: result.message });
    return true;
  }

  return {
    form,
    totals,
    isSubmitting,
    updateField,
    updateCreditRow,
    addCreditRow,
    removeCreditRow,
    updateTranche,
    addTranche,
    removeTranche,
    reset,
    submit,
  };
}
